// 8-bit Phoenix: the mascot sprite and the press-to-talk orb, both drawn on a
// canvas with no dependencies and no font files. Nostalgia is the point — Björn
// is an engineer who grew up on machines that drew in blocks — but legibility
// wins every tie: the sprite is decoration, never the only carrier of meaning.
//
// Sprite frames are a character grid; each char is a palette entry. They were
// designed and rendered offline, then pasted here as data.

type Grid = readonly string[];

const PALETTE: Record<string, string> = {
  k: "#8A240C", // deep outline ember
  d: "#C42B1C", // deep red
  r: "#F4531F", // red-orange
  o: "#FF8C1A", // orange
  g: "#FFC145", // gold
  w: "#FFF3D0", // white-hot
  e: "#3A1206", // eye
};

const FRAMES: Grid[] = [
  [ // flap 0
    "...................................",
    ".gg...........ro................gg.",
    ".oogg..........og.............ggoo.",
    ".rroogg........ggwgg........ggoorr.",
    ".drrroogg......gwweg......ggoorrrd.",
    ".kddrrooogg....gwwwgog..ggooorrddk.",
    "..dkdrrrooogg..gggggr.ggooorrrdkd..",
    "....ddrrroooogg.gwg.ggoooorrrdd....",
    ".....kddrrroooogwwwgoooorrrddk.....",
    "......dkdrrrrogwwwwwgorrrrdkd......",
    "........dddrrrgwwwwwgrrrddd........",
    ".........kdddrgwwwwwgrdddk.........",
    "..........dkddgwwwwwgddkd..........",
    "............ddgwwwwwgdd............",
    ".............kdgwwwgdk.............",
    "...............gwwwg...............",
    "...............ggggg...............",
    "..............g.ggg.g.....o........",
    ".....o.......o.o.ooo.o.............",
    "............r..o.ooo..r............",
    "...........d..o..oo.o..d.....o.....",
    "........o.....r..oo.r..............",
    ".............d...oo..d.............",
    ".................rr................",
  ],
  [ // flap 1
    ".gg.............................gg.",
    ".oogg.........ro..............ggoo.",
    ".rooo..........og.............ooor.",
    ".rrrogg........ggwgg........ggorrr.",
    ".drrroogg......gwweg......ggoorrrd.",
    ".kddrrooogg....gwwwgog..ggooorrddk.",
    "..dkdrrrooogg..gggggr.ggooorrrdkd..",
    "....ddrrroooogg.gwg.ggoooorrrdd....",
    ".....kddrrroooogwwwgoooorrrddk.....",
    "......dkdrrrrogwwwwwgorrrrdkd......",
    "........dddrrrgwwwwwgrrrddd........",
    ".........kdddrgwwwwwgrdddk.........",
    "..........dkddgwwwwwgddkd..........",
    "............ddgwwwwwgdd............",
    ".............kdgwwwgdk.............",
    "...............gwwwg...............",
    "...............ggggg...............",
    "..............g.ggg.g.....o........",
    ".....o.......o.o.ooo.o.............",
    "............r..o.ooo..r............",
    "...........d..o..oo.o..d.....o.....",
    "........o.....r..oo.r..............",
    ".............d...oo..d.............",
    ".................rr................",
  ],
  [ // flap 2
    ".oogg.........................ggoo.",
    ".oooo.........ro..............oooo.",
    ".rroogg........og...........ggoorr.",
    ".rrrroogg......ggwgg......ggoorrrr.",
    ".drrroooog.....gwweg.....goooorrrd.",
    ".dddrrrooog....gwwwgog..gooorrrddd.",
    ".kdddrrrroogg..gggggr.ggoorrrrdddk.",
    "..dkdddrrrooogg.gwg.ggooorrrdddkd..",
    "....dddddrroooogwwwgoooorrddddd....",
    ".....kdkddrrrogwwwwwgorrrddkdk.....",
    "........dddrrrgwwwwwgrrrddd........",
    ".........kdddrgwwwwwgrdddk.........",
    "..........dkddgwwwwwgddkd..........",
    "............ddgwwwwwgdd............",
    ".............kdgwwwgdk.............",
    "...............gwwwg...............",
    "...............ggggg...............",
    "..............g.ggg.g..............",
    ".............o.o.ooo.o....o........",
    ".....o......r..o.ooo..r............",
    "...........d..o..oo.o..d...........",
    "..............r..oo.r........o.....",
    "........o....d...oo..d.............",
    ".................rr................",
  ],
];

const SPRITE_W = FRAMES[0]![0]!.length;
const SPRITE_H = FRAMES[0]!.length;

const reduceMotion = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const OUTLINE = "#5E1704";

/**
 * Draws the sprite with a one-pixel dark outline. The outline is what lets the
 * same bird sit on a pale orb and on the night background without dissolving
 * into either — standard pixel-art practice, and here it is the difference
 * between a readable mascot and a smudge.
 */
function drawSprite(
  ctx: CanvasRenderingContext2D,
  frame: Grid,
  scale: number,
  ox: number,
  oy: number
): void {
  const solid = (x: number, y: number): boolean => {
    const row = frame[y];
    if (!row) return false;
    const ch = row[x];
    return ch !== undefined && ch !== ".";
  };

  ctx.fillStyle = OUTLINE;
  for (let y = 0; y < frame.length; y++) {
    const row = frame[y]!;
    for (let x = 0; x < row.length; x++) {
      if (solid(x, y)) continue;
      if (solid(x - 1, y) || solid(x + 1, y) || solid(x, y - 1) || solid(x, y + 1)) {
        ctx.fillRect(ox + x * scale, oy + y * scale, scale, scale);
      }
    }
  }

  for (let y = 0; y < frame.length; y++) {
    const row = frame[y]!;
    for (let x = 0; x < row.length; x++) {
      const color = PALETTE[row[x]!];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(ox + x * scale, oy + y * scale, scale, scale);
    }
  }
}

// Backing store at device resolution; the grid stays crisp because every cell
// is an integer number of device pixels and smoothing is off.
function fitCanvas(canvas: HTMLCanvasElement, cssW: number, cssH: number): CanvasRenderingContext2D {
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.scale(dpr, dpr);
  return ctx;
}

/** A gently flapping phoenix with drifting embers. Decorative: aria-hidden. */
export function mountPhoenix(host: HTMLElement, cssWidth = 210): () => void {
  const scale = Math.max(2, Math.floor(cssWidth / SPRITE_W));
  const w = SPRITE_W * scale;
  const h = (SPRITE_H + 3) * scale;
  const canvas = document.createElement("canvas");
  canvas.className = "phoenix-canvas";
  canvas.setAttribute("aria-hidden", "true");
  const ctx = fitCanvas(canvas, w, h);
  host.append(canvas);

  const embers = Array.from({ length: 7 }, (_, i) => ({
    x: (i * 5 + 3) % SPRITE_W,
    y: SPRITE_H + (i % 3),
    speed: 0.012 + (i % 4) * 0.004,
  }));

  let raf = 0;
  let t = 0;
  const still = reduceMotion();

  function draw(): void {
    ctx.clearRect(0, 0, w, h);
    // Wing beat: slow, with a longer glide at the top of the stroke.
    const cycle = (Math.sin(t * 0.9) + 1) / 2;
    const idx = still ? 1 : cycle < 0.34 ? 0 : cycle < 0.72 ? 1 : 2;
    const lift = still ? 0 : Math.round(Math.sin(t * 0.9) * scale * 0.5);
    drawSprite(ctx, FRAMES[idx]!, scale, 0, scale + lift);

    if (!still) {
      for (const em of embers) {
        em.y -= em.speed * 60;
        if (em.y < -2) {
          em.y = SPRITE_H + 2;
          em.x = (em.x + 7) % SPRITE_W;
        }
        const drift = Math.sin((em.y + em.x) * 0.4) * 0.8;
        ctx.fillStyle = em.y % 2 < 1 ? PALETTE["g"]! : PALETTE["o"]!;
        ctx.globalAlpha = Math.max(0, Math.min(1, em.y / SPRITE_H)) * 0.8;
        ctx.fillRect(
          Math.round((em.x + drift) * scale),
          Math.round(em.y * scale),
          scale,
          scale
        );
        ctx.globalAlpha = 1;
      }
    }
    t += 1 / 60;
    raf = requestAnimationFrame(draw);
  }

  draw();
  return () => cancelAnimationFrame(raf);
}

// ── the orb ────────────────────────────────────────────────────────────────
export type OrbPhase = "idle" | "listening" | "hearing" | "answering" | "off";

/**
 * The press-to-talk orb: a pixel sphere that holds the phoenix. It reports
 * state by colour AND motion, but never *only* by them — the caller keeps the
 * text label underneath, which is what a tired reader actually reads.
 */
export function mountOrb(host: HTMLElement, size = 232): {
  setPhase: (p: OrbPhase) => void;
  dispose: () => void;
} {
  const cell = Math.max(3, Math.floor(size / 46));
  const grid = Math.floor(size / cell);
  const px = grid * cell;
  const canvas = document.createElement("canvas");
  canvas.className = "orb-canvas";
  canvas.setAttribute("aria-hidden", "true");
  const ctx = fitCanvas(canvas, px, px);
  host.append(canvas);

  const centre = (grid - 1) / 2;
  const maxR = grid / 2 - 0.5;
  let phase: OrbPhase = "idle";
  let t = 0;
  let raf = 0;
  const still = reduceMotion();

  // Theme-dependent colours are read from CSS so the orb follows the palette.
  let ground = "#EAE6F0";
  let dim = "#FFEDE2";
  function readTheme(): void {
    const cs = getComputedStyle(host);
    ground = cs.getPropertyValue("--orb-ground").trim() || ground;
    dim = cs.getPropertyValue("--orb-dim").trim() || dim;
  }
  readTheme();
  const themeQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const onTheme = (): void => readTheme();
  themeQuery.addEventListener("change", onTheme);

  const sprScale = Math.max(1, Math.floor((grid * 0.62) / SPRITE_W));
  const sprOX = Math.round((grid - SPRITE_W * sprScale) / 2) * cell;
  const sprOY = Math.round((grid - SPRITE_H * sprScale) / 2) * cell;

  function draw(): void {
    ctx.clearRect(0, 0, px, px);
    const beat = still ? 0.5 : (Math.sin(t * 1.6) + 1) / 2;

    // radius and rim behaviour per phase
    const grow =
      phase === "listening" ? 0.06 + beat * 0.03 : phase === "answering" ? 0.03 : 0;
    const r = maxR * (0.9 + grow) - (phase === "off" ? 1 : 0);

    for (let gy = 0; gy < grid; gy++) {
      for (let gx = 0; gx < grid; gx++) {
        const dx = gx - centre;
        const dy = gy - centre;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > r) continue;
        const edge = dist / r;
        let color: string;
        if (phase === "off") {
          color = edge > 0.86 ? ground : dim;
        } else if (edge > 0.88) {
          // rim: a bright segment travels round it while it is hearing you out
          const ang = (Math.atan2(dy, dx) + Math.PI) / (Math.PI * 2);
          const chase = still ? 0 : (t * 0.28) % 1;
          const near = Math.abs(((ang - chase + 1.5) % 1) - 0.5);
          color =
            phase === "idle"
              ? ground
              : phase === "listening"
                ? PALETTE["w"]!
                : phase === "hearing"
                  ? near > 0.4
                    ? PALETTE["g"]!
                    : PALETTE["o"]!
                  : PALETTE["g"]!;
        } else if (phase === "listening") {
          // holding the button: the orb is lit right through — unmistakably live
          color = edge > 0.72 ? PALETTE["o"]! : PALETTE["g"]!;
        } else if (phase === "answering") {
          // concentric waves travelling outward while the persona speaks
          const wave = (Math.sin(edge * 9 - t * 4) + 1) / 2;
          color = wave > 0.55 ? PALETTE["o"]! : dim;
        } else if (phase === "hearing") {
          const wave = (Math.sin(edge * 6 - t * 2.2) + 1) / 2;
          color = wave > 0.7 ? PALETTE["g"]! : dim;
        } else {
          color = dim;
        }
        ctx.fillStyle = color;
        ctx.fillRect(gx * cell, gy * cell, cell, cell);
      }
    }

    // the bird rides in the middle, flapping only when there is something to say
    const active = phase === "listening" || phase === "answering";
    const cycle = (Math.sin(t * (phase === "listening" ? 3.2 : 1.4)) + 1) / 2;
    const idx = still || !active ? 1 : cycle < 0.34 ? 0 : cycle < 0.72 ? 1 : 2;
    ctx.globalAlpha = phase === "off" ? 0.5 : 1;
    drawSprite(ctx, FRAMES[idx]!, sprScale * cell, sprOX, sprOY);
    ctx.globalAlpha = 1;

    t += 1 / 60;
    raf = requestAnimationFrame(draw);
  }

  draw();

  return {
    setPhase(p: OrbPhase): void {
      phase = p;
    },
    dispose(): void {
      cancelAnimationFrame(raf);
      themeQuery.removeEventListener("change", onTheme);
    },
  };
}
